import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const BUCKET_NAME = 'news_images';

const BeritaAdmin = () => {
    const { userRole } = useAuth();
    const isAdminNasional = userRole === 'admin_nasional';

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [form, setForm] = useState({ title: '', content: '' });
    const fileInputRef = useRef(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const { data: result, error } = await supabase
                .from('berita')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setData(result || []);
        } catch (error) {
            console.error('Error fetching berita:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Ekstraktor Nama File dari URL Publik
    const extractFileName = (url) => {
        if (!url) return null;
        const parts = url.split(`${BUCKET_NAME}/`);
        return parts.length > 1 ? parts[1] : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAdminNasional) return;

        const file = fileInputRef.current.files[0];
        if (!file) return alert('Silakan pilih gambar terlebih dahulu.');

        // Validasi Klien: Ekstensi Gambar & Max 2MB
        if (!file.type.startsWith('image/')) {
            return alert('Format file harus berupa gambar (JPEG, PNG, dll).');
        }
        if (file.size > 2 * 1024 * 1024) {
            return alert('Ukuran gambar maksimal 2MB.');
        }

        try {
            setIsSubmitting(true);

            // 1. Upload ke Storage
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from(BUCKET_NAME)
                .upload(fileName, file);

            if (uploadError) throw new Error(`Upload gagal: ${uploadError.message}`);

            // 2. Dapatkan URL
            const { data: urlData } = supabase.storage
                .from(BUCKET_NAME)
                .getPublicUrl(fileName);

            // 3. Insert ke Database
            const { error: dbError } = await supabase
                .from('berita')
                .insert([{
                    title: form.title,
                    content: form.content,
                    image_url: urlData.publicUrl
                }]);

            if (dbError) throw new Error(`Gagal menyimpan database: ${dbError.message}`);

            setForm({ title: '', content: '' });
            fileInputRef.current.value = ''; // Reset input file
            fetchData();
            alert('Berita berhasil ditambahkan!');

        } catch (error) {
            console.error('Error proses berita:', error);
            alert(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id, imageUrl) => {
        if (!isAdminNasional || !window.confirm('Yakin ingin menghapus berita ini beserta gambarnya?')) return;

        try {
            // 1. Ekstrak nama file & Hapus dari Storage
            const fileName = extractFileName(imageUrl);
            if (fileName) {
                const { error: storageError } = await supabase.storage
                    .from(BUCKET_NAME)
                    .remove([fileName]);

                if (storageError) console.warn('Gagal menghapus file storage (mungkin sudah terhapus manual):', storageError);
            }

            // 2. Hapus baris Database
            const { error: dbError } = await supabase
                .from('berita')
                .delete()
                .eq('id', id);

            if (dbError) throw dbError;

            setData((prev) => prev.filter((item) => item.id !== id));
        } catch (error) {
            console.error('Error deleting berita:', error);
            alert('Gagal menghapus berita.');
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Manajemen Berita & Artikel</h1>
            <p className="text-gray-600 mb-6">Kelola konten publikasi untuk portal utama.</p>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Form Tambah */}
                {isAdminNasional && (
                    <div className="w-full lg:w-1/3 bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-fit">
                        <h2 className="text-lg font-semibold mb-4 text-gray-700">Tulis Berita Baru</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Judul Berita</label>
                                <input
                                    type="text"
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    placeholder="Masukkan judul menarik"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Gambar Utama (Maks: 2MB)</label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    required
                                    accept="image/*"
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Isi Konten</label>
                                <textarea
                                    required
                                    rows={5}
                                    value={form.content}
                                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                    placeholder="Tulis paragraf berita..."
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-blue-600 text-white font-medium py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-70 transition-colors"
                            >
                                {isSubmitting ? 'Mengunggah & Menyimpan...' : 'Tayangkan Berita'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Tabel Data (Read-Only) */}
                <div className={`w-full ${isAdminNasional ? 'lg:w-2/3' : 'w-full'} bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden`}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gambar</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Konten</th>
                                    {isAdminNasional && <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr><td colSpan={isAdminNasional ? 3 : 2} className="px-6 py-8 text-center text-gray-500">Memuat berita...</td></tr>
                                ) : data.length === 0 ? (
                                    <tr><td colSpan={isAdminNasional ? 3 : 2} className="px-6 py-8 text-center text-gray-500">Belum ada berita dipublikasikan.</td></tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="h-16 w-24 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <span className="text-xs text-gray-400 p-2">Tanpa Gambar</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                                                <p className="text-sm text-gray-500 truncate max-w-xs">{item.content}</p>
                                                <span className="text-xs text-gray-400 block mt-1">{new Date(item.created_at).toLocaleDateString('id-ID')}</span>
                                            </td>
                                            {isAdminNasional && (
                                                <td className="px-6 py-4 text-sm font-medium text-center">
                                                    <button
                                                        onClick={() => handleDelete(item.id, item.image_url)}
                                                        className="text-red-600 hover:text-red-900 transition-colors"
                                                    >
                                                        Hapus
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BeritaAdmin;